/**
 * Tool Registry
 *
 * Central registry for all tools in the application.
 * Provides methods to register, retrieve, and query tools.
 */

import { isSwitchedOn } from "../utils/toolAccess";
import { RedirectsTool } from "./redirects/RedirectsTool";
import { BannersTool } from "./banners/BannersTool";
import { PromotersTool } from "./promoters/PromotersTool";
import { ResourceOrdersTool } from "./orders/ResourceOrdersTool";
import { PromoterHubTool } from "./promoterHub/PromoterHubTool";
import { PropertiesTool } from "./properties/PropertiesTool";
import { UsersTool } from "./users/UsersTool";
import { ConfigTool } from "./config/ConfigTool";
import { OthersTool } from "./others/OthersTool";
import { EngagementTool } from "./engagement/EngagementTool";
import { SocialTool } from "./social/SocialTool";
import { HealthTool } from './health/HealthTool';
import { AnalyticsTool } from './analytics/AnalyticsTool';
import { CampaignsTool } from './campaigns/CampaignsTool';
import { SupportTool } from './support/SupportTool';
// Future tools can be imported here

class ToolRegistry {
  constructor() {
    /** @type {Map<string, BaseTool>} */
    this.tools = new Map();
    this.initialized = false;
  }

  /**
   * Initialize registry with default tools
   * Called automatically on first use
   */
  init() {
    if (this.initialized) return;

    try {
      // ⚠️ Registration order IS the sidebar order — there is no sort anywhere,
      // so a tool added at the end lands at the end and the nav drifts by
      // accident. Ordered deliberately below; add new tools INTO the sequence,
      // not after it.
      //
      // Grouped by what they are about, outward-in: where customers came from,
      // then who they are, then what we run.
      //   (Map is rendered by the sidebar itself, ahead of these.)
      this.register(new AnalyticsTool()); //   audience: everything, reimagined (beta, hidden by default)
      this.register(new EngagementTool()); //  audience: traffic + behaviour
      this.register(new RedirectsTool()); //   audience: the links that bring it
      this.register(new PromoterHubTool()); // people: promoters + their output
      this.register(new PropertiesTool()); //  people: the property pipeline
      this.register(new CampaignsTool()); //   outbound: what we send, and what came of it (beta, hidden by default)
      this.register(new SupportTool()); //     who needs an answer now (off for standard users until switched on)
      this.register(new SocialTool()); //      outbound: beta
      this.register(new HealthTool()); //      our own estate (admin, mainNav)

      // navHidden — registered for backup/export granularity and detail routes,
      // never shown in the nav. Their order is irrelevant.
      this.register(new BannersTool());
      this.register(new PromotersTool());
      this.register(new ResourceOrdersTool());
      this.register(new OthersTool());

      // System group.
      this.register(new UsersTool());
      this.register(new ConfigTool());

      this.initialized = true;
    } catch (error) {
      console.error("❌ Failed to initialize Tool Registry:", error);
      throw error;
    }
  }

  /**
   * Register a tool
   * @param {BaseTool} tool - Tool instance to register
   */
  register(tool) {
    // Validate tool
    tool.validate();

    // Check for duplicate IDs - skip if already registered (for hot reload compatibility)
    if (this.tools.has(tool.id)) {
      console.warn(`Tool with id '${tool.id}' is already registered, skipping`);
      return;
    }

    this.tools.set(tool.id, tool);
  }

  /**
   * Get a tool by ID
   * @param {string} id - Tool ID
   * @returns {BaseTool|undefined}
   */
  get(id) {
    if (!this.initialized) this.init();
    return this.tools.get(id);
  }

  /**
   * Get all registered tools
   * @returns {BaseTool[]}
   */
  getAll() {
    if (!this.initialized) this.init();
    return Array.from(this.tools.values());
  }

  /**
   * Get tools filtered by criteria
   * @param {object} criteria - Filter criteria
   * @param {boolean} criteria.requiresAdmin - Filter by admin requirement
   * @param {boolean} criteria.hasCollections - Only tools with collections
   * @returns {BaseTool[]}
   */
  filter(criteria = {}) {
    if (!this.initialized) this.init();

    let tools = this.getAll();

    if (criteria.requiresAdmin !== undefined) {
      tools = tools.filter(
        (tool) => tool.requiresAdmin === criteria.requiresAdmin
      );
    }

    if (criteria.hasCollections) {
      tools = tools.filter((tool) => tool.collections.length > 0);
    }

    return tools;
  }

  /**
   * Get tools with their metadata for backup system
   * @returns {Array<{id: string, name: string, description: string, collections: string[]}>}
   */
  getForBackup() {
    if (!this.initialized) this.init();

    return this.getAll()
      .filter((tool) => tool.collections.length > 0 || tool.id === "others")
      .map((tool) => ({
        id: tool.id,
        name: tool.name,
        description: tool.description,
        collections: tool.getCollections(), // Use getCollections() for dynamic lists
        icon: tool.icon,
        color: tool.color,
      }));
  }

  /**
   * Get all summary data from all tools
   * Useful for dashboard overview
   * @returns {Promise<{[toolId: string]: object}>}
   */
  async getAllSummaries() {
    if (!this.initialized) this.init();

    const summaries = {};
    const promises = [];

    for (const [id, tool] of this.tools) {
      if (tool.summaryConfig.enabled) {
        promises.push(
          tool
            .getSummaryData()
            .then((data) => {
              summaries[id] = data;
            })
            .catch((error) => {
              console.error(`Failed to get summary for ${id}:`, error);
              summaries[id] = { success: false, error: error.message };
            })
        );
      }
    }

    await Promise.all(promises);
    return summaries;
  }

  /**
   * Get tools accessible by a user
   * @param {object} user - User object with isAdmin property
   * @returns {BaseTool[]}
   */
  getAccessibleTools(user, shownTools = []) {
    if (!this.initialized) this.init();
    return this.getAll().filter((tool) => !tool.navHidden && tool.canAccess(user)
      && (!tool.hiddenByDefault || shownTools.includes(tool.id)));
  }

  /**
   * Get tools visible to a user (considering global permissions and user preferences)
   * @param {object} user - User object with isAdmin property
   * @param {object} globalPermissions - Global tool permissions from settings (e.g., { redirects: true, banners: false })
   * @param {string[]} hiddenTools - Array of tool IDs the user has hidden
   * @param {string[]} shownTools - hidden-by-default tools the user has switched ON
   * @returns {BaseTool[]}
   */
  getVisibleTools(user, globalPermissions = {}, hiddenTools = [], shownTools = []) {
    if (!this.initialized) this.init();

    return this.getAll().filter((tool) => {
      // Reachable only as a tab of another tool — never listed on its own.
      if (tool.navHidden) return false;

      // Beta tools stay out of sight — admins included — until switched on in the
      // person's profile. Checked before the admin short-cut below on purpose.
      if (tool.hiddenByDefault && !shownTools.includes(tool.id)) return false;

      // Admin-only tools are ALWAYS visible to admins (ignore permissions)
      if (tool.requiresAdmin && user?.isAdmin) {
        return true;
      }

      // Check if user has access (role-based)
      if (!tool.canAccess(user)) {
        return false;
      }

      // Admins can override global permissions - only check their hidden list
      if (user?.isAdmin) {
        return !hiddenTools.includes(tool.id);
      }

      // Regular users: check the tool's switch first (src/utils/toolAccess.js: a tool may start
      // off until an administrator switches it on), then the hidden list
      if (!isSwitchedOn(globalPermissions, tool)) {
        return false;
      }

      if (hiddenTools.includes(tool.id)) {
        return false;
      }

      return true;
    });
  }

  /**
   * Check if registry has a tool
   * @param {string} id - Tool ID
   * @returns {boolean}
   */
  has(id) {
    if (!this.initialized) this.init();
    return this.tools.has(id);
  }

  /**
   * Get registry statistics
   * @returns {object}
   */
  getStats() {
    if (!this.initialized) this.init();

    const tools = this.getAll();
    return {
      total: tools.length,
      withSummary: tools.filter((t) => t.summaryConfig.enabled).length,
      requiresAdmin: tools.filter((t) => t.requiresAdmin).length,
      withCollections: tools.filter((t) => t.collections.length > 0).length,
      totalCollections: tools.reduce((sum, t) => sum + t.collections.length, 0),
    };
  }

  /**
   * Export all tool metadata as JSON
   * Useful for debugging and documentation
   */
  toJSON() {
    if (!this.initialized) this.init();
    return Object.fromEntries(
      Array.from(this.tools.entries()).map(([id, tool]) => [id, tool.toJSON()])
    );
  }
}

// Export singleton instance and class
export { ToolRegistry };
export const toolRegistry = new ToolRegistry();
export default toolRegistry;
